import React from 'react'
import classNames from 'classnames'

import { BUMP_UP, getColorTheme, mq, LegacyColor, Icon, renderIconSVG } from '../../../utils'
import Image from 'next/image'
import { Text } from '../../Text'
import { IProps } from '../index'

function getCTAColor(cardColor: LegacyColor) {
  switch (cardColor) {
    case LegacyColor.LightBlue:
      return LegacyColor.Blue

    case LegacyColor.BlueExtraLight:
      return LegacyColor.Blue

    case LegacyColor.LightGray:
      return LegacyColor.Black
  }
}

export const CardSharp: React.FC<Omit<IProps, 'openMessenger'>> = ({
  eyebrow,
  title,
  body,
  cta,
  icon,
  isHiddenOnMobile,
  alignment = 'stretch',
  cardColor,
}) => {
  const themeColors = getColorTheme('default', cardColor)
  const ctaColor = getCTAColor(cardColor as LegacyColor)

  const classes = classNames('card card--sharp', {
    [`card--align-${alignment}`]: alignment,
  })

  return (
    <div className={classes} data-testid="card">
      {eyebrow && (
        <div className="card__eyebrow">
          <Text brand="default" size="eyebrows">
            {eyebrow}
          </Text>
        </div>
      )}
      {icon && (
        <div className="card__icon">
          <Image
            loader={(image) => image.src}
            alt={icon.altText}
            src={icon.url}
            height={icon.height || 0}
            width={icon.width || 0}
          />
        </div>
      )}
      <h4 className="card__title">
        <Text brand="default" size="heading-4-bold">
          {title}
        </Text>
      </h4>

      {body && (
        <div className="card__body">
          <Text brand="default" size="body">
            {body.trim()}
          </Text>
        </div>
      )}

      {cta ? (
        <div className="card__cta">
          <Text brand="default" size="button-bold">
            {cta.text}
          </Text>
          {cta.arrow && renderIconSVG(Icon.Arrow, 'cta-link__arrow')}
        </div>
      ) : null}

      <style jsx>{`
        .card {
          display: flex;
          flex-direction: column;
          padding: ${icon || !cta ? '34px 38px' : '46px 40px'};
          color: ${themeColors.textColor};
          background-color: ${themeColors.backgroundColor};
          cursor: ${cta ? 'pointer' : 'default'};
          min-height: ${cta ? '100%' : 'none'};
          height: 100%;

          &__eyebrow {
            margin-bottom: 1.5rem;
          }

          &__icon {
            justify-self: ${alignment};
            width: 70px;
          }

          &__title {
            margin-bottom: ${body ? '12px' : '33px'};
          }

          &__body {
            margin-bottom: ${cta ? '1.3em' : '0'};
          }

          &__cta {
            color: ${ctaColor};
          }

          &--align-center {
            align-items: center;
            text-align: center;
          }
        }

        @media (${mq.mobile}) {
          .card {
            display: ${isHiddenOnMobile ? 'none' : 'flex'};
          }
        }

        @media (${mq.tablet}) {
          .card {
            transition: ${BUMP_UP.TRANSITION};

            &:hover {
              transform: ${BUMP_UP.TRANSFORM};
            }
          }
        }
      `}</style>

      <style jsx>
        {`
          .card {
            border-radius: 6px;
            transform: translate3d(0, 0, 0);

            &__icon {
              margin-bottom: 1.3em;
            }

            &__body {
              line-height: 1.4;
            }
          }

          @media (${mq.tablet}) {
            .card__cta {
              margin-top: auto;
            }
          }
        `}
      </style>
    </div>
  )
}
